import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, MapPin, Truck, AlertTriangle, Check, X, Clock, Activity, Zap, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface AutoTrip {
  id: string;
  vehicle_id: string;
  vehicle_code: string | null;
  vehicle_plate: string | null;
  driver_id: string | null;
  driver_name: string | null;
  start_site_id: string | null;
  start_site_name: string | null;
  end_site_id: string | null;
  end_site_name: string | null;
  start_lat: number;
  start_lng: number;
  end_lat: number;
  end_lng: number;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  distance_km: number;
  confidence: number;
  status: string;
  created_at: string;
}

interface DetectionStats {
  pending: number;
  confirmed: number;
  rejected: number;
  high_conf: number;
  medium_conf: number;
  low_conf: number;
  recent_runs: Array<{
    id: string; started_at: string; ended_at: string | null;
    vehicles_analyzed: number; candidates_detected: number; status: string;
  }>;
}

const REJECT_REASONS = ['false_positive', 'duplicate_of_manual', 'personal_use', 'maintenance_run', 'other'] as const;
type RejectReason = typeof REJECT_REASONS[number];

const HIGH_CONF_THRESHOLD = 0.85;

const fmtDateTime = (iso: string) => new Date(iso).toLocaleString('en-GB', { hour12: false });
const fmtDuration = (min: number) => min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${min % 60}m`;

function ConfidenceBadge({ value }: { value: number }) {
  const tone =
    value >= HIGH_CONF_THRESHOLD ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' :
    value >= 0.60                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400' :
                                   'bg-rose-500/15 text-rose-700 dark:text-rose-400';
  const label = value >= HIGH_CONF_THRESHOLD ? 'HIGH' : value >= 0.60 ? 'MEDIUM' : 'LOW';
  return (
    <span className={'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + tone}>
      {label} · {Math.round(value * 100)}%
    </span>
  );
}

export default function AutoDetectedTripsPage() {
  const { t } = useTranslation();
  const [list, setList] = useState<AutoTrip[]>([]);
  const [stats, setStats] = useState<DetectionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confidenceFilter, setConfidenceFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [vehicleFilter, setVehicleFilter] = useState<string>('all');
  const [running, setRunning] = useState(false);

  // Confirm-with-edit modal
  const [editing, setEditing] = useState<AutoTrip | null>(null);
  const [overrideQty, setOverrideQty] = useState<string>('');
  const [submittingConfirm, setSubmittingConfirm] = useState(false);

  // Reject modal
  const [rejecting, setRejecting] = useState<AutoTrip | null>(null);
  const [rejectReason, setRejectReason] = useState<RejectReason>('false_positive');
  const [rejectNotes, setRejectNotes] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);

  // Bulk selection
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        apiClient.get<AutoTrip[]>('/api/v1/trip-detection/pending'),
        apiClient.get<DetectionStats>('/api/v1/trip-detection/stats'),
      ]);
      setList(p.data);
      setStats(s.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const allVehicles = useMemo(() => {
    const m = new Map<string, string>();
    for (const tr of list) {
      if (tr.vehicle_code) m.set(tr.vehicle_id, `${tr.vehicle_code} · ${tr.vehicle_plate ?? ''}`);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [list]);

  const filtered = useMemo(() => list.filter(tr => {
    if (confidenceFilter === 'high'   && tr.confidence < HIGH_CONF_THRESHOLD) return false;
    if (confidenceFilter === 'medium' && (tr.confidence < 0.60 || tr.confidence >= HIGH_CONF_THRESHOLD)) return false;
    if (confidenceFilter === 'low'    && tr.confidence >= 0.60) return false;
    if (vehicleFilter !== 'all' && tr.vehicle_id !== vehicleFilter) return false;
    return true;
  }), [list, confidenceFilter, vehicleFilter]);

  // Confidence summary across the unfiltered pending list
  const summary = useMemo(() => {
    let high = 0, mid = 0, low = 0;
    for (const tr of list) {
      if (tr.confidence >= HIGH_CONF_THRESHOLD) high++;
      else if (tr.confidence >= 0.60) mid++;
      else low++;
    }
    return { high, mid, low };
  }, [list]);

  const selectedTrips = useMemo(() => list.filter(tr => selected.has(tr.id)), [list, selected]);
  const bulkAllowed = selectedTrips.length > 0 && selectedTrips.every(tr => tr.confidence >= HIGH_CONF_THRESHOLD);

  function toggleSelected(id: string) {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelected() { setSelected(new Set()); }

  function openConfirmModal(tr: AutoTrip) {
    setEditing(tr);
    setOverrideQty('');
  }

  async function submitConfirm() {
    if (!editing) return;
    setSubmittingConfirm(true);
    setBusyId(editing.id);
    try {
      const override: Record<string, unknown> = {};
      if (overrideQty.trim() !== '') {
        const n = Number(overrideQty);
        if (!Number.isNaN(n) && n >= 0) override.deliveredQtyM3 = n;
      }
      const body = Object.keys(override).length > 0 ? { override } : undefined;
      await apiClient.post('/api/v1/trip-detection/' + editing.id + '/confirm', body);
      setEditing(null);
      setOverrideQty('');
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setSubmittingConfirm(false);
      setBusyId(null);
    }
  }

  function openRejectModal(tr: AutoTrip) {
    setRejecting(tr);
    setRejectReason('false_positive');
    setRejectNotes('');
  }

  async function submitReject() {
    if (!rejecting) return;
    setSubmittingReject(true);
    setBusyId(rejecting.id);
    try {
      const payload: Record<string, unknown> = { reason: rejectReason };
      if (rejectNotes.trim()) payload.notes = rejectNotes.trim();
      await apiClient.post('/api/v1/trip-detection/' + rejecting.id + '/reject', payload);
      setRejecting(null);
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setSubmittingReject(false);
      setBusyId(null);
    }
  }

  async function bulkConfirm() {
    if (!bulkAllowed) return;
    if (!window.confirm(t('autoTrips.bulkConfirmPrompt', { defaultValue: `Confirm ${selectedTrips.length} high-confidence trips?`, count: selectedTrips.length }))) return;
    setBulkRunning(true);
    let okCount = 0; let failCount = 0;
    for (const tr of selectedTrips) {
      try {
        await apiClient.post('/api/v1/trip-detection/' + tr.id + '/confirm');
        okCount++;
      } catch {
        failCount++;
      }
    }
    setBulkRunning(false);
    clearSelected();
    await refresh();
    window.alert(`Confirmed ${okCount} of ${selectedTrips.length}.${failCount > 0 ? ` ${failCount} failed.` : ''}`);
  }

  async function runNow() {
    if (!window.confirm(t('autoTrips.runNowPrompt', { defaultValue: 'Run trip detection on all active vehicles for the past 24 hours?' }))) return;
    setRunning(true);
    try {
      const r = await apiClient.post<{ vehicles_analyzed: number; candidates_detected: number }>('/api/v1/trip-detection/run-now', { lookbackHours: 24 });
      window.alert(`Run complete. Analyzed ${r.data.vehicles_analyzed} vehicles, detected ${r.data.candidates_detected} candidates.`);
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
    finally { setRunning(false); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Activity className="h-4 w-4" /> {t('autoTrips.title', { defaultValue: 'Auto-detected trips' })}</div>
          <Button size="sm" variant="outline" onClick={runNow} disabled={running} className="gap-2">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} {t('autoTrips.runNow', { defaultValue: 'Run now' })}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Confidence summary header */}
        {list.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm flex items-center gap-4 flex-wrap">
            <span className="font-medium">{t('autoTrips.confidenceSummary', { defaultValue: 'Pending review:' })}</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <strong>{summary.high}</strong> {t('autoTrips.confHighLabel', { defaultValue: 'high-confidence (auto-confirmable)' })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              <strong>{summary.mid}</strong> {t('autoTrips.confMediumLabel', { defaultValue: 'medium' })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-rose-500" />
              <strong>{summary.low}</strong> {t('autoTrips.confLowLabel', { defaultValue: 'low' })}
            </span>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Stat label="Pending" value={String(stats.pending)} tone={stats.pending > 0 ? 'warn' : undefined} />
            <Stat label="Confirmed" value={String(stats.confirmed)} tone="good" />
            <Stat label="Rejected" value={String(stats.rejected)} />
            <Stat label="High conf." value={String(stats.high_conf)} />
            <Stat label="Medium conf." value={String(stats.medium_conf)} />
            <Stat label="Low conf." value={String(stats.low_conf)} />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <select value={confidenceFilter} onChange={(e) => setConfidenceFilter(e.target.value as typeof confidenceFilter)}
            className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
            <option value="all">All confidence levels</option>
            <option value="high">High (≥85%)</option>
            <option value="medium">Medium (60–84%)</option>
            <option value="low">Low (&lt;60%)</option>
          </select>
          <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)}
            className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
            <option value="all">All vehicles</option>
            {allVehicles.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm">
              <strong>{selected.size}</strong> {t('autoTrips.selected', { defaultValue: 'selected', count: selected.size })}
              {!bulkAllowed && (
                <span className="ms-2 text-xs text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {t('autoTrips.bulkRequiresHigh', { defaultValue: 'Bulk confirm requires every selected trip to be ≥85% confidence' })}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearSelected} disabled={bulkRunning}>
                {t('common.clear', { defaultValue: 'Clear' })}
              </Button>
              <Button size="sm" onClick={bulkConfirm} disabled={!bulkAllowed || bulkRunning} className="gap-1.5">
                {bulkRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {t('autoTrips.confirmSelected', { defaultValue: 'Confirm selected' })}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
            {list.length === 0
              ? t('autoTrips.empty', { defaultValue: 'No auto-detected trips pending review. The detection cron runs every 30 minutes — candidates appear here as GPS data accumulates.' })
              : t('autoTrips.emptyFiltered', { defaultValue: 'No trips match these filters.' })}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((tr) => {
              const isSelected = selected.has(tr.id);
              return (
                <div key={tr.id} className={`rounded-lg border p-4 space-y-3 transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'border-border'}`}>
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelected(tr.id)}
                        className="h-4 w-4 rounded border-input"
                        aria-label={`Select trip ${tr.vehicle_code ?? tr.id.slice(0, 8)}`}
                      />
                      <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-mono text-sm">{tr.vehicle_code ?? tr.vehicle_id.slice(0, 8)}</span>
                      {tr.vehicle_plate && <span className="text-xs text-muted-foreground">{tr.vehicle_plate}</span>}
                      {tr.driver_name && <span className="text-xs text-muted-foreground">· {tr.driver_name}</span>}
                    </div>
                    <ConfidenceBadge value={tr.confidence} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="space-y-1">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Start</div>
                      <div>
                        {tr.start_site_name ? <strong>{tr.start_site_name}</strong> : <span className="text-muted-foreground italic">Unknown location</span>}
                        <div className="text-xs text-muted-foreground font-mono">{tr.start_lat.toFixed(5)}, {tr.start_lng.toFixed(5)}</div>
                        <div className="text-xs text-muted-foreground">{fmtDateTime(tr.start_time)}</div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> End</div>
                      <div>
                        {tr.end_site_name ? <strong>{tr.end_site_name}</strong> : <span className="text-muted-foreground italic">Unknown location</span>}
                        <div className="text-xs text-muted-foreground font-mono">{tr.end_lat.toFixed(5)}, {tr.end_lng.toFixed(5)}</div>
                        <div className="text-xs text-muted-foreground">{fmtDateTime(tr.end_time)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDuration(tr.duration_minutes)}</span>
                      <span>· {tr.distance_km.toFixed(1)} km</span>
                      <span>· detected {fmtDateTime(tr.created_at)}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openRejectModal(tr)} disabled={busyId === tr.id} className="gap-1.5">
                        <X className="h-3.5 w-3.5" /> Reject
                      </Button>
                      <Button size="sm" onClick={() => openConfirmModal(tr)} disabled={busyId === tr.id} className="gap-1.5">
                        {busyId === tr.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        Confirm & promote
                      </Button>
                    </div>
                  </div>

                  <a href={`https://www.openstreetmap.org/?mlat=${tr.start_lat}&mlon=${tr.start_lng}#map=12/${tr.start_lat}/${tr.start_lng}`}
                     target="_blank" rel="noreferrer"
                     className="text-xs text-muted-foreground underline hover:text-foreground">
                    Open start point in map ↗
                  </a>
                </div>
              );
            })}
          </div>
        )}

        {stats && stats.recent_runs.length > 0 && (
          <details className="rounded-lg border border-border" open>
            <summary className="cursor-pointer px-4 py-2 text-sm font-medium bg-muted/30">
              Recent detection runs ({stats.recent_runs.length})
            </summary>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-muted/20 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Started</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Vehicles</th>
                    <th className="text-right px-3 py-2">Candidates</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_runs.map((r) => (
                    <tr key={r.id} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2 text-xs">{fmtDateTime(r.started_at)}</td>
                      <td className="px-3 py-2 text-xs">{r.status}</td>
                      <td className="px-3 py-2 text-right">{r.vehicles_analyzed}</td>
                      <td className="px-3 py-2 text-right">{r.candidates_detected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </main>

      {/* Confirm-with-edit modal */}
      <Sheet open={!!editing} onOpenChange={(o) => !o && !submittingConfirm && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('autoTrips.confirmTitle', { defaultValue: 'Confirm & promote to trip' })}</SheetTitle>
            <SheetDescription>
              {editing && (
                <>
                  {editing.vehicle_code} · {editing.vehicle_plate} · <ConfidenceBadge value={editing.confidence} />
                </>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Bilingual "is this correct" banner */}
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm space-y-1">
              <p className="font-medium inline-flex items-center gap-2 text-foreground">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                {t('autoTrips.verifyBanner.en', { defaultValue: 'Verify the detected trip details before confirming. Once confirmed, this becomes a paid trip and feeds into invoicing.' })}
              </p>
              <p className="text-xs text-muted-foreground" dir="rtl">
                {t('autoTrips.verifyBanner.ar', { defaultValue: 'تحقّق من تفاصيل الرحلة قبل التأكيد. عند التأكيد ستصبح رحلة مدفوعة وتدخل في الفوترة.' })}
              </p>
            </div>

            {editing && (
              <div className="space-y-3 text-sm">
                <Row label="Vehicle"  value={`${editing.vehicle_code ?? '?'} · ${editing.vehicle_plate ?? ''}`} />
                <Row label="Driver"   value={editing.driver_name ?? '—'} />
                <Row label="Start"    value={editing.start_site_name ?? `${editing.start_lat.toFixed(4)}, ${editing.start_lng.toFixed(4)}`} />
                <Row label="End"      value={editing.end_site_name ?? `${editing.end_lat.toFixed(4)}, ${editing.end_lng.toFixed(4)}`} />
                <Row label="When"     value={`${fmtDateTime(editing.start_time)} → ${fmtDateTime(editing.end_time)}`} />
                <Row label="Duration" value={fmtDuration(editing.duration_minutes)} />
                <Row label="Distance" value={`${editing.distance_km.toFixed(1)} km`} />

                <div className="pt-3 border-t border-border space-y-1.5">
                  <Label htmlFor="overrideQty">
                    {t('autoTrips.overrideQty', { defaultValue: 'Delivered qty (m³)' })}
                  </Label>
                  <Input
                    id="overrideQty"
                    type="number" step="0.5" min={0} max={100} dir="ltr"
                    value={overrideQty}
                    onChange={(e) => setOverrideQty(e.target.value)}
                    placeholder={t('autoTrips.overrideQtyPlaceholder', { defaultValue: 'Leave blank to use detected default' })}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t('autoTrips.overrideQtyHint', { defaultValue: 'Optional. Adjust if the detected volume is off (e.g. partial fill).' })}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={submittingConfirm}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="button" onClick={submitConfirm} disabled={submittingConfirm}>
              {submittingConfirm && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('autoTrips.confirmAndPromote', { defaultValue: 'Confirm & promote' })}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Reject modal with reason dropdown */}
      <Sheet open={!!rejecting} onOpenChange={(o) => !o && !submittingReject && setRejecting(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('autoTrips.rejectTitle', { defaultValue: 'Reject detected trip' })}</SheetTitle>
            <SheetDescription>
              {rejecting && <>{rejecting.vehicle_code} · {rejecting.vehicle_plate}</>}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
            <div className="space-y-1.5">
              <Label htmlFor="rejectReason">{t('autoTrips.reason', { defaultValue: 'Reason' })}</Label>
              <select
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value as RejectReason)}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
              >
                {REJECT_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`autoTrips.reasonOption.${r}`, { defaultValue: r.replace(/_/g, ' ') })}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rejectNotes">{t('autoTrips.notes', { defaultValue: 'Notes (optional)' })}</Label>
              <textarea
                id="rejectNotes"
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base"
                maxLength={500}
              />
            </div>
          </div>

          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRejecting(null)} disabled={submittingReject}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="button" variant="outline" onClick={submitReject} disabled={submittingReject}>
              {submittingReject && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('autoTrips.rejectConfirm', { defaultValue: 'Reject' })}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  const cls = tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : '';
  return (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-xl font-medium mt-0.5 ' + cls}>{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
